import axios from 'axios';
import axiosIntents from '@/utils/axios-intents'

import i18n from '@/i18n'

import { MEGA_BASE_2 } from '@/consts'

const API_V2 = 'v2';

import formatDate from '@/helpers/date-helpers/format-date'
import StoreCache from '@/utils/StoreCache.js'

const DEFAULT_LIFETIME_USAGE = 1 * 60 * 60 * 1000 // 1 hour

function parseDatePreservingTimezone(date_string) {
    const match = date_string.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2}):(\d{2})/)
    if (!match) return new Date(date_string)

    const [, y, mo, d, h, mi, s] = match
    return new Date(+y, +mo - 1, +d, +h, +mi, +s)
}

const USAGE_CHARGE_TYPE_CONFIG = {
    data: { charged_field: 'ChargedData', usage_type: 'data', unit: 'MB' },
    mins: { charged_field: 'ChargedMins', usage_type: 'voice', unit: 'minutes' },
    txts: { charged_field: 'ChargedTXTs', usage_type: 'sms', unit: 'SMS' },
    pxts: { charged_field: 'ChargedPXTs', usage_type: 'mms', unit: 'MMS' },
}

function mapAggregatedCharge(charge, type_config, aggregate_mode) {
    const date = parseDatePreservingTimezone(charge.DateAndTime)
    const year = formatDate(date, 'YYYY')
    const month = formatDate(date, 'MMMM')
    const day = formatDate(date, 'D')
    const week_name = formatDate(date, 'ddd')

    const base = {
        TotalAmountCents: charge.AmountCents,
        TotalChargedUsage: charge[type_config.charged_field],
        usage_type: type_config.usage_type,
        unit: type_config.unit,
        year,
        event_time: date.getTime(),
    }

    if (aggregate_mode === 'day') {
        return { ...base, month, day, week_name, title: `${week_name} ${day} ${month} ${year}` }
    }

    return { ...base, month, title: `${month} ${year}` }
}

function aggregateByYear(monthly_charges) {
    return monthly_charges.reduce((acc, curr) => {
        const existing = acc.find(u => u.year === curr.year)

        if (existing) {
            existing.TotalAmountCents += curr.TotalAmountCents
            existing.TotalChargedUsage += curr.TotalChargedUsage
        } else {
            acc.push({
                TotalAmountCents: curr.TotalAmountCents,
                TotalChargedUsage: curr.TotalChargedUsage,
                usage_type: curr.usage_type,
                unit: curr.unit,
                year: curr.year,
                title: curr.year,
            })
        }

        return acc
    }, [])
}

const USAGE_TYPES = {
    'Data MB': { title: i18n.t('data'),  icon: 'data-usage',  type: 'Data MB',  is_integer: false,  unit: i18n.t('unit.MB'), usage_key: 'data'    },
    'TetheredData MB': { title: i18n.t('data'),  icon: 'data-usage',  type: 'Data MB',  is_integer: false,  unit: i18n.t('unit.MB'), usage_key: 'tetheringData'    },
    Voice:     { title: i18n.t('voice'), icon: 'phone-usage', type: 'Voice',    is_integer: true,   unit: i18n.t('unit.mins'), usage_key: 'voice' },
    SMS:       { title: i18n.t('SMS'),   icon: 'sms-usage',   type: 'SMS',      is_integer: true,   unit: i18n.t('unit.SMS'), usage_key: 'sms'    },
    MMS:       { title: i18n.t('MMS'),   icon: 'mms-usage',   type: 'MMS',      is_integer: true,   unit: i18n.t('unit.MMS'), usage_key: 'mms'    },
};

const USAGE_TYPES_FWA = {
    'Data MB': { title: i18n.t('data'),  icon: 'data-usage',  type: 'Data MB',  is_integer: false,  unit: i18n.t('unit.MB'), usage_key: 'data'    },
};

const USAGE_TYPES_UFB = {
    'Data MB': { title: i18n.t('data'),  icon: 'data-usage',  type: 'Data MB',  is_integer: false,  unit: i18n.t('unit.MB'), usage_key: 'data'    },
};

const USAGE_GROUPS = [
    { title: i18n.t('daily_upper'),   icon: 'calendar', group_key: 'day'   },
    { title: i18n.t('monthly_upper'), icon: 'calendar', group_key: 'month' },
    { title: i18n.t('yearly_upper'),  icon: 'calendar', group_key: 'year'  },
];

const USAGES = {
	'Data MB': {
	    history: {},
	    id: 'Data MB',
	    credit: 'CreditDataMB',
	    icon: 'data-usage',
	    total: 'TotalDataMB',
	    title: i18n.t('data'),
	    type: 'Data MB',
	    unit: i18n.t('unit.MB'),
	    kind: 'data',
	    is_integer: false,
	},
	/* Voice: {
	    history: {},
	    id: 'Voice',
	    credit: 'CreditVoiceMinutes',
	    icon: 'phone-usage',
	    total: 'TotalVoiceMinutes',
	    title: i18n.t('voice'),
	    type: 'Voice',
	    unit: i18n.t('unit.mins'),
	    kind: 'mins',
	    is_integer: true,
	},
	SMS: {
	    history: {},
	    id: 'SMS',
	    credit: 'CreditSMS',
	    icon: 'sms-usage',
	    total: 'TotalSMS',
	    title: i18n.t('SMS'),
	    type: 'SMS',
	    unit: i18n.t('unit.SMS'),
	    kind: 'txts',
	    is_integer: true,
	},
	MMS: {
	    history: {},
	    id: 'MMS',
	    credit: 'CreditMMS',
	    icon: 'mms-usage',
	    total: 'TotalMMS',
	    title: i18n.t('MMS'),
	    type: 'MMS',
	    unit: i18n.t('unit.MMS'),
	    kind: 'pxts',
	    is_integer: true,
	}, */
};

const DEFAULT_BUCKET = Array.from({ length: 30 }).fill(0);

const pending_aggregated_requests = new Map()

const USAGE_TYPE_TO_API_TYPE = {
    data: 'data',
    voice: 'mins',
    sms: 'txts',
    mms: 'pxts',
}

const AGGREGATE_MODE_MAP = {
    day: 'day',
    month: 'month',
    year: 'month',
}

const state = {
    usage: {},
    is_getting_usage: false,

    usage_charges: new StoreCache(DEFAULT_LIFETIME_USAGE),
    usage_charges_loaded_types: [],
    is_getting_usage_charges: false,

    usage_charges_aggregated: {},
    usage_charges_generation: 0,
    aggregated_charges_in_flight: 0,
    is_getting_aggregated_charges: false,
};

const getters = {
    usage_types: (state) => USAGE_TYPES,
    usage_types_fwa: (state) => USAGE_TYPES_FWA,
    usage_types_ufb: (state) => USAGE_TYPES_UFB,
    usage_groups: (state) => USAGE_GROUPS,
    is_getting_usage: (state) => state.is_getting_usage,
    is_getting_usage_charges: (state) => state.is_getting_usage_charges,
    usage: (state) => state.usage,

    usage_charges: (state) => state.usage_charges.cachedData() || [],

    has_usage_charges: (state, getters) => Boolean(getters.usage_charges.length),

    usage_data_charges: (state, getters) => getters.usage_charges.filter(usage_charge => usage_charge.ChargedData),
    usage_voice_charges: (state, getters) => getters.usage_charges.filter(usage_charge => usage_charge.ChargedMins),
    usage_sms_charges: (state, getters) => getters.usage_charges.filter(usage_charge => usage_charge.ChargedTXTs),
    usage_mms_charges: (state, getters) => getters.usage_charges.filter(usage_charge => usage_charge.ChargedPXTs),

    is_getting_aggregated_charges: (state) => state.is_getting_aggregated_charges,

    aggregated_charges_by_key: (state) => (cache_key) => {
        const cache = state.usage_charges_aggregated[cache_key]
        return cache ? (cache.cachedData() || []) : []
    },

    usage_data_charges_by_day: (state, getters) => getters.aggregated_charges_by_key('data_day'),
    usage_data_charges_by_month: (state, getters) => getters.aggregated_charges_by_key('data_month'),
    usage_data_charges_by_year: (state, getters) => aggregateByYear(getters.aggregated_charges_by_key('data_month')),

    usage_voice_charges_by_day: (state, getters) => getters.aggregated_charges_by_key('voice_day'),
    usage_voice_charges_by_month: (state, getters) => getters.aggregated_charges_by_key('voice_month'),
    usage_voice_charges_by_year: (state, getters) => aggregateByYear(getters.aggregated_charges_by_key('voice_month')),

    usage_sms_charges_by_day: (state, getters) => getters.aggregated_charges_by_key('sms_day'),
    usage_sms_charges_by_month: (state, getters) => getters.aggregated_charges_by_key('sms_month'),
    usage_sms_charges_by_year: (state, getters) => aggregateByYear(getters.aggregated_charges_by_key('sms_month')),

    usage_mms_charges_by_day: (state, getters) => getters.aggregated_charges_by_key('mms_day'),
    usage_mms_charges_by_month: (state, getters) => getters.aggregated_charges_by_key('mms_month'),
    usage_mms_charges_by_year: (state, getters) => aggregateByYear(getters.aggregated_charges_by_key('mms_month')),
};

const mutations = {
    SAVE_USAGES(state, usages) {
        state.usage = usages;
    },

    SAVE_USAGE_CHARGES(state, { usage_charges, loaded_types }) {
        state.usage_charges.fix(usage_charges)
        state.usage_charges_loaded_types = loaded_types
    },

    RESET_USAGE_CHARGES(state) {
        state.usage_charges.reset()
        state.usage_charges_loaded_types = []

        Object.values(state.usage_charges_aggregated).forEach(cache => cache.reset())
        state.usage_charges_aggregated = {}
        state.usage_charges_generation++
        pending_aggregated_requests.clear()
    },

    SET_IS_GETTING_USAGES(state, is_getting_usage) {
        state.is_getting_usage = is_getting_usage;
    },

    SET_IS_GETTING_USAGE_CHARGES(state, is_getting_usage_charges) {
        state.is_getting_usage_charges = is_getting_usage_charges;
    },

    SAVE_AGGREGATED_CHARGES(state, { cache_key, charges }) {
        if (!state.usage_charges_aggregated[cache_key]) {
            state.usage_charges_aggregated = {
                ...state.usage_charges_aggregated,
                [cache_key]: new StoreCache(DEFAULT_LIFETIME_USAGE),
            }
        }
        state.usage_charges_aggregated[cache_key].fix(charges)
    },

    RESET_AGGREGATED_CHARGES(state, cache_key) {
        state.usage_charges_aggregated[cache_key]?.reset()
    },

    SET_IS_GETTING_AGGREGATED_CHARGES(state, value) {
        state.aggregated_charges_in_flight = Math.max(0, state.aggregated_charges_in_flight + (value ? 1 : -1))
        state.is_getting_aggregated_charges = state.aggregated_charges_in_flight > 0
    },
};

const actions = {
    async getUsageCharges({ state, getters, commit }, { FromNanos, ToNanos, usage_type } = {}) {
        const ALL_USAGE_CHARGE_TYPES = Object.keys(USAGE_CHARGE_TYPE_CONFIG)
        const api_type = usage_type ? USAGE_TYPE_TO_API_TYPE[usage_type] : null
        const types_to_fetch = api_type ? [api_type] : ALL_USAGE_CHARGE_TYPES

        const has_explicit_range = Boolean(FromNanos || ToNanos)
        const is_cached = !has_explicit_range
            && !state.usage_charges.expired()
            && types_to_fetch.every(type => state.usage_charges_loaded_types.includes(type))
        if (is_cached) return getters.usage_charges

        commit('SET_IS_GETTING_USAGE_CHARGES', true)

        const generation = state.usage_charges_generation

        const defaultFromNanos = getters.customer_current_product?.CreatedAtNanos || 0
        const defaultUptoNanos = new Date().getTime() * 1_000_000

        const From = FromNanos || defaultFromNanos
        const Upto = ToNanos || defaultUptoNanos
        const CustomerUUID = getters.current_account_uid
        const MobileUUID = getters.customer_current_mobile_product_uuid

        try {
            const results = await Promise.allSettled(
                types_to_fetch.map(type =>
                    axiosIntents.get(
                        `/api/customer/v2/${CustomerUUID}/cellular/usage-charges/${MobileUUID}`,
                        { params: { From, Upto, type } }
                    )
                )
            )
            if (generation !== state.usage_charges_generation) return []

            const all_charges = results.flatMap(result => {
                if (result.status !== 'fulfilled') return []
                const data = result.value?.data
                return Array.isArray(data) ? data : []
            })

            if (!all_charges.length) {
                commit('SAVE_USAGE_CHARGES', { usage_charges: [], loaded_types: types_to_fetch })
                return []
            }

            const usage_charges_mapped = all_charges.map(usage_charge => {
                const date_and_time = parseDatePreservingTimezone(usage_charge.DateAndTime)
    
                const map_obj = {
                    ...usage_charge,
                    DateAndTimeUTC: usage_charge.DateAndTime,
                    date_and_time: date_and_time,
                    date: formatDate(date_and_time, 'D MMM YYYY'),
                    year: formatDate(date_and_time, 'YYYY'),
                    month: formatDate(date_and_time, 'MMMM'),
                    day: formatDate(date_and_time, 'D'),
                    week_name: formatDate(date_and_time, 'ddd'),
                    time_with_day_part: formatDate(date_and_time, 'hh:mm A'),
                    event_time: date_and_time.getTime(),
                }
    
                delete map_obj.DateAndTime
    
                return map_obj
            })
    
            usage_charges_mapped.sort((a, b) => b.event_time - a.event_time)
    
            commit('SAVE_USAGE_CHARGES', { usage_charges: usage_charges_mapped, loaded_types: types_to_fetch })

            return usage_charges_mapped
        } catch (error) {
            if (generation !== state.usage_charges_generation) return []
            commit('RESET_USAGE_CHARGES')
            return []
        } finally {
            commit('SET_IS_GETTING_USAGE_CHARGES', false)
        }
    },

    async fetchUsageChargesBySelection({ state, getters, commit }, { usage_type, group_type } = {}) {
        if (!usage_type || !group_type) return

        const api_type = USAGE_TYPE_TO_API_TYPE[usage_type]
        const api_aggregate = AGGREGATE_MODE_MAP[group_type]
        if (!api_type || !api_aggregate) return

        const cache_key = `${usage_type}_${api_aggregate}`
        const cache = state.usage_charges_aggregated[cache_key]
        if (cache && !cache.expired() && Array.isArray(cache.cachedData())) return

        if (pending_aggregated_requests.has(cache_key)) return pending_aggregated_requests.get(cache_key)

        const request = (async () => {
            commit('SET_IS_GETTING_AGGREGATED_CHARGES', true)
            const generation = state.usage_charges_generation

            const defaultFromNanos = getters.customer_current_product?.CreatedAtNanos || 0
            const defaultUptoNanos = new Date().getTime() * 1_000_000

            const CustomerUUID = getters.current_account_uid
            const MobileUUID = getters.customer_current_mobile_product_uuid

            const type_config = USAGE_CHARGE_TYPE_CONFIG[api_type]

            try {
                const response = await axiosIntents.get(
                    `/api/customer/v2/${CustomerUUID}/cellular/usage-charges/${MobileUUID}`,
                    { params: { From: defaultFromNanos, Upto: defaultUptoNanos, type: api_type, aggregate: api_aggregate } }
                )
                if (generation !== state.usage_charges_generation) return

                const data = Array.isArray(response?.data) ? response.data : []
                const mapped = data.map(charge => mapAggregatedCharge(charge, type_config, api_aggregate))
                mapped.sort((a, b) => b.event_time - a.event_time)

                commit('SAVE_AGGREGATED_CHARGES', { cache_key, charges: mapped })
            } catch (error) {
                if (generation === state.usage_charges_generation) {
                    commit('RESET_AGGREGATED_CHARGES', cache_key)
                }
            } finally {
                pending_aggregated_requests.delete(cache_key)
                commit('SET_IS_GETTING_AGGREGATED_CHARGES', false)
            }
        })()

        pending_aggregated_requests.set(cache_key, request)
        return request
    },

    async getUsageByTypeAndRange({ getters, commit, dispatch }, { period, ProductType }) {
	    commit('SAVE_USAGES', {});
	    commit('SET_IS_GETTING_USAGES', true);

        const date_now = new Date(new Date().setHours(0, 0, 0, 0)).getTime();

	    const body = {
	    	CustomerUUID: getters.current_account_uid,
	    	ProductInstanceUUID: getters.customer_current_mobile_product_uuid,
	    	ProductType,
	    	Range: {
	    		FirstNanos: (date_now - period * 24 * 60 * 60 * 1000) * 1000000,
	    		FinalNanos: (date_now - 1000) * 1000000,
	    	  },
	    	IntervalNanos: 24 * 60 * 60 * 1000 * 1000000,
	    };

        const stats_promises = Object.values(USAGES)
            .map(({ kind }) => kind)
            .map(kind => axios.post(`${API_V2}/:spid/usage/${kind}/histogram`, body));

        const rslt = await Promise.allSettled(stats_promises);
        const usages_keys = Object.keys(USAGES);
        const usage_date = new Date(new Date().getFullYear(), new Date().getMonth() , new Date().getDate());
        const usage_dates = Array.from({ length: 30 }, (_) => new Date(usage_date.setDate(usage_date.getDate() - 1)));

        const usages_with_stats = rslt.reduce((acc, curr, index) => {
            const { status } = curr;

            let bucket = DEFAULT_BUCKET;

            if (status === 'fulfilled' && curr.value.result.Buckets) {
                if (acc[usages_keys[index]]['kind'] === 'data') {
                    bucket = curr.value.result.Buckets.map(val => +(val / MEGA_BASE_2).toFixed(2)).reverse();
                } else if (acc[usages_keys[index]]['kind'] === 'mins') {
                    bucket = curr.value.result.Buckets.map(val => +(val / 60).toFixed(0)).reverse();
                } else {
                    bucket = [...curr.value.result.Buckets].reverse();
                }
            }

            bucket.forEach((val, i) => {
                acc[usages_keys[index]]['history'][usage_dates[i].toDateString()] = {
                    date: usage_dates[i],
                    usage: val,
                    timestamp: usage_dates[i].getTime(),
                };
            });

            return acc
            
        }, { ...USAGES });


	    commit('SET_IS_GETTING_USAGES', false);
        commit('SAVE_USAGES', usages_with_stats);
    },

    async getUFBUsageByTypeAndRange({ getters, commit, dispatch }, { period, ProductType }) {
        commit('SAVE_USAGES', {});
        commit('SET_IS_GETTING_USAGES', true);

        const date_now = new Date(new Date().setHours(0, 0, 0, 0)).getTime();

        const body = {
        	CustomerUUID: getters.current_account_uid,
        	ProductInstanceUUID: getters.ufb_product?.UUID,
        	ProductType,
        	Range: {
        		FirstNanos: (date_now - period * 24 * 60 * 60 * 1000) * 1000000,
        		FinalNanos: (date_now - 1000) * 1000000,
        	  },
        	IntervalNanos: 24 * 60 * 60 * 1000 * 1000000,
        };

        try {
            const rslt = await axiosIntents.post(`/api/customers/v1/${getters.current_account_uid}/usage-histogram`, body);
            const usages_keys = Object.keys(USAGES);
            const usage_date = new Date(new Date().getFullYear(), new Date().getMonth() , new Date().getDate());
            const usage_dates = Array.from({ length: 30 }, (_) => new Date(usage_date.setDate(usage_date.getDate() - 1)));

            const usages_with_stats = { ...USAGES };
            
            let bucket = DEFAULT_BUCKET;

            if (rslt && rslt.data && rslt.data.Buckets) {
                bucket = rslt.data.Buckets.map(val => +(val / MEGA_BASE_2).toFixed(2)).reverse();
            }

            // Apply the bucket data to the first usage type (Data MB)
            const firstUsageKey = usages_keys[0];
            bucket.forEach((val, i) => {
                usages_with_stats[firstUsageKey]['history'][usage_dates[i].toDateString()] = {
                    date: usage_dates[i],
                    usage: val,
                    timestamp: usage_dates[i].getTime(),
                };
            });

            commit('SAVE_USAGES', usages_with_stats);
        } catch (error) {
            console.log('getUFBUsageByTypeAndRange error', error);
            commit('SAVE_USAGES', { ...USAGES });
        } finally {
            commit('SET_IS_GETTING_USAGES', false);
        }
    },
};

export default {
    state,
    getters,
    mutations,
    actions,
};
